import { memo, useMemo } from "react";
import { useTheme } from "@mui/material";
import { PolygonF } from "@react-google-maps/api";
import { Polygon, MultiPolygon } from "geojson";
import { UnitAvailability } from "@ses-mams/api-contract";
import { useUnitAvailabilityData } from "../hooks";

export const UnitAvailabilityPolygons = memo(() => {
  const { polygons, unitAvailabilityMap } = useUnitAvailabilityData();

  if (!polygons) {
    return null;
  }

  return polygons.features.map(feature => (
    <UnitPolygon
      key={feature.id}
      polygon={feature.geometry}
      availability={unitAvailabilityMap.get(feature.properties.unitId)}
    />
  ));
});

type UnitPolygonProps = {
  availability?: UnitAvailability;
  polygon: Polygon | MultiPolygon;
};

const THRESHOLD = 0.2;

const UnitPolygon = ({ availability, polygon }: UnitPolygonProps) => {
  const {
    tokens: { colors },
  } = useTheme();

  const paths = useMemo(() => {
    if (polygon.type === "MultiPolygon") {
      return polygon.coordinates.map(polygonCoords =>
        polygonCoords[0].map(([lng, lat]) => ({
          lat,
          lng,
        }))
      );
    }

    // Handle regular Polygon
    return polygon.coordinates.map(ring =>
      ring.map(([lng, lat]) => ({
        lat,
        lng,
      }))
    );
  }, [polygon]);

  const immediatelyAvailable = availability?.immediatelyAvailable ?? 0;
  const available = availability?.available ?? 0;
  const conditional = availability?.conditional ?? 0;
  const unavailable = availability?.unavailable ?? 0;
  const totalMembers = availability?.totalMembers ?? 0;

  const fillColor = useMemo(() => {
    if (!totalMembers) {
      return colors.background.neutral;
    }

    if ((immediatelyAvailable + available) / totalMembers >= THRESHOLD) {
      return colors.background.positive;
    }

    if (conditional / totalMembers >= THRESHOLD) {
      return colors.background.caution;
    }

    if (unavailable / totalMembers >= THRESHOLD) {
      return colors.background.critical;
    }

    return colors.background.neutral;
  }, [
    colors,
    immediatelyAvailable,
    available,
    conditional,
    unavailable,
    totalMembers,
  ]);

  return (
    <PolygonF
      paths={paths}
      options={{
        fillColor,
        strokeColor: "white",
        strokeWeight: 1,
        fillOpacity: 0.75,
      }}
    />
  );
};
